import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuAction {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  separator?: boolean;
  /** Hover tooltip, when the label alone doesn't say where the action leads. */
  title?: string;
}

interface ContextMenuProps {
  x: number;
  y: number;
  /** Names what was right-clicked; shown as a non-interactive title row. */
  header?: string;
  actions: ContextMenuAction[];
  onClose: () => void;
}

const MARGIN = 8;

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, header, actions, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: y, left: x });

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - MARGIN) {
      left = Math.max(MARGIN, window.innerWidth - rect.width - MARGIN);
    }
    if (top + rect.height > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, window.innerHeight - rect.height - MARGIN);
    }
    setPosition({ top, left });
    // Recompute once children are laid out; x/y only change on reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleActionClick = (action: ContextMenuAction) => {
    if (action.disabled) return;
    action.onSelect();
    onClose();
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={header}
      data-fieldmenu-surface
      className="fixed z-[9999] min-w-[160px] rounded-md border border-theme-border-primary bg-theme-bg-secondary py-1 shadow-lg"
      style={{ top: position.top, left: position.left }}
    >
      {header && (
        <div
          data-menu-header
          className="mb-1 truncate border-b border-theme-border-secondary px-3 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400"
        >
          {header}
        </div>
      )}
      {actions.map((action, index) => (
        <React.Fragment key={action.label}>
          {action.separator && index > 0 && (
            <div className="my-1 border-t border-theme-border-secondary" />
          )}
          <button
            type="button"
            role="menuitem"
            title={action.title}
            disabled={action.disabled}
            onClick={() => handleActionClick(action)}
            className="block w-full truncate px-3 py-1.5 text-left text-[0.8125rem] text-slate-700 hover:bg-theme-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200"
          >
            {action.label}
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body,
  );
};
